import { Block, Flex, RadioGroup, Typography } from '@aloft/toolkit-sui';
import { SortingState, Table } from '@tanstack/react-table';

import { Dispatch, SetStateAction, useState } from 'react';

import { useImageCarousel } from '../../../../hooks/useCarousel/useImageCarousel.ts';
import { ImageCarousel } from '../../../ImageCarousel/ImageCarousel.tsx';
import { PinStatusFilter } from '../../../map/types.ts';
import { useCompMap } from '../../hooks/useCompMap.ts';
import { CompSelectionProperty, CompSelectionState } from '../../types.ts';

type PropertyCompSelectionDetailsProps = {
  property: Pick<CompSelectionProperty, 'id' | 'imageUrls' | 'address' | 'latitude' | 'longitude'>;
  subject: Pick<CompSelectionProperty, 'latitude' | 'longitude'>;
  data: CompSelectionProperty[];
  comps: CompSelectionProperty[];
  compSelectionTableRef?: React.MutableRefObject<Table<CompSelectionProperty> | undefined>;
  sorting: SortingState;
  setFocusedRow: Dispatch<SetStateAction<string | undefined>>;
  rowSelection?: CompSelectionState;
  isInspectifyOrder?: boolean;
  hasArvOption?: boolean;
  selectedStatuses: PinStatusFilter[];
  onSelectedStatusesChange: (statuses: PinStatusFilter[]) => void;
};

export const PropertyCompSelectionDetails = ({
  property: { id, imageUrls = [], address, latitude = 0, longitude = 0 },
  subject: { latitude: subjectLat = 0, longitude: subjectLng = 0 },
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  rowSelection,
  isInspectifyOrder,
  hasArvOption,
  selectedStatuses,
  onSelectedStatusesChange,
}: PropertyCompSelectionDetailsProps) => {
  const [selectedImageIx, selectNextImage, selectPreviousImage] = useImageCarousel(
    imageUrls,
    0,
    10,
  );
  const [mapType, setMapType] = useState<string>('satellite');

  const { mapContainerRef } = useCompMap({
    selectedPropertyId: id,
    lat: latitude,
    lng: longitude,
    subjectLat,
    subjectLng,
    data,
    comps,
    compSelectionTableRef,
    sorting,
    setFocusedRow,
    mapType,
    rowSelection,
    isInspectifyOrder,
    hasArvOption,
    selectedStatuses,
    onSelectedStatusesChange,
  });

  const imageLabel = `Image ${selectedImageIx + 1} of ${imageUrls.length}`;

  const handleChangeMapType = (value: string) => {
    setMapType(value);
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      overflowY="scroll"
      backgroundColor="surface1"
      margin="0.5rem"
      borderRadius="0.375rem"
      justifyContent="stretch"
      height="calc(100% - 1rem)"
      position="relative"
    >
      <Block padding="0.5rem">
        <Typography variant="h2">{address?.AddressLineText ?? ''}</Typography>
      </Block>

      {imageUrls.length > 0 && (
        <>
          <Block height="20rem" width="100%" overflow="hidden">
            <ImageCarousel
              imageUrls={imageUrls}
              selectedImageIx={selectedImageIx}
              selectPreviousImage={selectPreviousImage}
              selectNextImage={selectNextImage}
              previousDisabled={selectedImageIx === 0}
              nextDisabled={selectedImageIx === imageUrls.length - 1}
              arrowSize="lg"
            />
          </Block>
          <Typography variant="placeholder">{imageLabel}</Typography>
        </>
      )}

      <Flex position="absolute" bottom="40px" right="20px">
        <Block zIndex="1" backgroundColor="surface1" padding="10px" borderRadius="8px">
          <RadioGroup
            value={mapType}
            items={[
              { value: 'satellite', label: 'Satellite' },
              { value: 'street', label: 'Street' },
            ]}
            size="md"
            onChange={handleChangeMapType}
          />
        </Block>
      </Flex>
      <Block height="100%" width="100%">
        <Block ref={mapContainerRef} height="100%" width="100%" />
      </Block>
    </Flex>
  );
};
